
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';

interface AccountFormProps {
  mode: 'signup' | 'login';
  onToggleMode: () => void;
}

const AccountForm: React.FC<AccountFormProps> = ({ mode, onToggleMode }) => {
  const { sendMagicLink, signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Generate a random password when component mounts (only for signup)
  React.useEffect(() => {
    if (mode === 'signup' && !password) {
      const generateRandomPassword = () => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
        let result = '';
        for (let i = 0; i < 12; i++) {
          result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        setPassword(result);
      };
      
      generateRandomPassword();
    }
  }, [mode, password]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        title: "Email required",
        description: "Please enter your email address.",
        variant: "destructive"
      });
      return;
    }

    if (mode === 'login' && !password) {
      toast({
        title: "Password required",
        description: "Please enter your password.",
        variant: "destructive"
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      if (mode === 'signup') {
        // Send a magic link to the provided email address with redirect to results page
        await sendMagicLink(email, `${window.location.origin}/results?email=${encodeURIComponent(email)}`);
        
        toast({
          title: "Verification email sent!",
          description: "Check your email to complete your account setup.",
        });
      } else {
        // Log in existing user
        await signIn(email, password);
        
        toast({
          title: "Login successful!",
          description: "Your results have been saved to your account.",
        });
      }
    } catch (error: any) {
      console.error("Error with authentication:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to process your request. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
          Email Address
        </label>
        <Input 
          id="email"
          type="email" 
          placeholder="your.email@example.com" 
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="w-full"
        />
      </div>
      
      {mode === 'login' && (
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
            Password
          </label>
          <div className="relative">
            <Input 
              id="password"
              type={showPassword ? "text" : "password"} 
              placeholder="Password" 
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full pr-20"
            />
            <button 
              type="button"
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-500"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>
      )}
      
      <div className="pt-2">
        <Button 
          type="submit" 
          className="w-full bg-morgen-gradient text-white hover:opacity-90 transition-opacity"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Processing..." : mode === 'signup' 
            ? "Email me a sign-up link" 
            : "Sign In"}
        </Button>
      </div>
      
      <div className="text-center pt-2">
        <button 
          type="button"
          onClick={onToggleMode}
          className="text-morgenBlue hover:underline text-sm"
        >
          {mode === 'signup' 
            ? "Already have an account? Sign in" 
            : "Need an account? Sign up"}
        </button>
      </div>
    </form>
  );
};

export default AccountForm;
